import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MAT_BUTTON_CONFIG } from '@angular/material/button';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import {
  ApiModule as ContractApi,
  Configuration as ContractApiConfig,
} from 'contractapi-ts-ng-client';
import {
  ApiModule as DocumentApi,
  Configuration as DocumentApiConfig,
} from 'document-ts-ng-client';
import { ApiModule as FileApi, Configuration as FileApiConfig } from 'file-ts-ng-client';
import { ApiModule as MetricApi, Configuration as MetricApiConfig } from 'metricsapi-ts-ng-client';
import {
  ApiModule as NSClientApi,
  Configuration as NSClientApiConfig,
} from 'nsclient-ts-ng-client';
import {
  ApiModule as NSInvoiceApi,
  Configuration as NSInvoiceApiConfig,
} from 'nsinvoice-ts-ng-client';
import {
  ApiModule as OrganizationApi,
  Configuration as OrganizationConfig,
} from 'org-ts-ng-client';
import { ApiModule as ProjectApi, Configuration as ProjectConfig } from 'projectapi-ts-ng-client';
import { ApiModule as ServiceApi, Configuration as ServiceApiConfig } from 'service-ts-ng-client';
import { environment } from 'src/environments/environment';
import { ApiModule as TrackerApi, Configuration as TrackerApiConfig } from 'tracker-ts-ng-client';
import { ApiModule as UserApi, Configuration as UserConfig } from 'user-ts-ng-client';
import { ApiModule as VendorApi, Configuration as VendorApiConfig } from 'vendorapi-ts-ng-client';

import { APP_ICONS } from '@shared/components/icon/icon.map';
import { AuthService } from '@core/auth/auth.service';
import { InsightsService } from '@core/insights/insights.service';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { CorrelationInterceptor } from '@core/interceptors/correlation.interceptor';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';

import { routes } from './app.routes';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './msal.config';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  provideIcons,
  provideNgIconsConfig,
  withContentSecurityPolicy,
  withExceptionLogger,
} from '@ng-icons/core';
import {
  ApiModule as ClassificationEngineApi,
  Configuration as ClassificationEngineConfig,
} from '@srm-biztech/classification-engine-client';
import {
  ApiModule as ContractOrchestrationApi,
  Configuration as ContractOrchestrationApiConfig,
} from '@srm-biztech/contract-orchestration-engine-client';
import {
  ApiModule as InvoiceBillingResolverApi,
  Configuration as InvoiceBillingResolverApiConfig,
} from '@srm-biztech/invoice-billing-resolver-client';
import {
  ApiModule as ServiceVendorTaxonomyApi,
  Configuration as ServiceVendorTaxonomyConfig,
} from '@srm-biztech/service-vendor-taxonomy-client';

export const initializeMsal =
  (msalInstance: IPublicClientApplication, authService: AuthService): (() => Promise<void>) =>
  async () => {
    await msalInstance.initialize();

    if (await authService.startPasswordResetIfRequested()) {
      return;
    }

    await authService.initialize();
  };

export function initializeApp(insightsService: InsightsService) {
  return (): void => {
    insightsService.startTracking();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
    ),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideIcons(APP_ICONS),
    provideNgIconsConfig({}, withContentSecurityPolicy(), withExceptionLogger()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE, AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [InsightsService],
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: MAT_BUTTON_CONFIG,
      useValue: {
        disabledInteractive: true,
      },
    },
    MsalService,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationInterceptor,
      multi: true,
    },
    importProvidersFrom([
      ClassificationEngineApi.forRoot(
        () =>
          new ClassificationEngineConfig({
            basePath: environment.apiConfig.classificationEngineApiUrl,
          }),
      ),
      TrackerApi.forRoot(
        () =>
          new TrackerApiConfig({
            basePath: environment.apiConfig.trackerApiUrl,
          }),
      ),
      FileApi.forRoot(
        () =>
          new FileApiConfig({
            basePath: environment.apiConfig.fileApiUrl,
          }),
      ),
      UserApi.forRoot(
        () =>
          new UserConfig({
            basePath: environment.apiConfig.userApiUrl,
          }),
      ),
      OrganizationApi.forRoot(
        () =>
          new OrganizationConfig({
            basePath: environment.apiConfig.organizationApiUrl,
          }),
      ),
      ProjectApi.forRoot(
        () =>
          new ProjectConfig({
            basePath: environment.apiConfig.projectApiUrl,
          }),
      ),
      ContractApi.forRoot(
        () =>
          new ContractApiConfig({
            basePath: environment.apiConfig.contractApiUrl,
          }),
      ),
      NSInvoiceApi.forRoot(
        () =>
          new NSInvoiceApiConfig({
            basePath: environment.apiConfig.nsInvoiceApiUrl,
          }),
      ),
      NSClientApi.forRoot(
        () =>
          new NSClientApiConfig({
            basePath: environment.apiConfig.nsClientApiUrl,
          }),
      ),
      MetricApi.forRoot(
        () =>
          new MetricApiConfig({
            basePath: environment.apiConfig.metricApiUrl,
          }),
      ),
      VendorApi.forRoot(
        () =>
          new VendorApiConfig({
            basePath: environment.apiConfig.vendorApiUrl,
          }),
      ),
      ServiceApi.forRoot(
        () =>
          new ServiceApiConfig({
            basePath: environment.apiConfig.serviceApiUrl,
          }),
      ),
      ContractOrchestrationApi.forRoot(
        () =>
          new ContractOrchestrationApiConfig({
            basePath: environment.apiConfig.contractOrchestrationApiUrl,
          }),
      ),
      InvoiceBillingResolverApi.forRoot(
        () =>
          new InvoiceBillingResolverApiConfig({
            basePath: environment.apiConfig.invoiceBillingResolverApiUrl,
          }),
      ),
      ServiceVendorTaxonomyApi.forRoot(
        () =>
          new ServiceVendorTaxonomyConfig({
            basePath: environment.apiConfig.serviceVendorTaxonomyApiUrl,
          }),
      ),
      DocumentApi.forRoot(
        () =>
          new DocumentApiConfig({
            basePath: environment.apiConfig.documentApiUrl,
          }),
      ),
    ]),
  ],
};
